'use client';

import Image from 'next/image';
import { useEffect, useRef } from 'react';
import type { CSSProperties } from 'react';
import { IconXMark } from '@/components/icons/IconXMark';
import UiModal from '@/components/ui/UiModal';
import { resolveImageAlt, sanityImage } from '@/lib/sanity/image';
import type { UiMediaLightboxProps } from './UiMediaLightbox.types';

/**
 * The film, full size — the one video modal on the site. A Mediathek tile opens
 * into it, so does the hero's showreel button: one box, one caption, one close
 * button, defined here and nowhere else.
 *
 * It takes the film rather than the thing that opened it, which is what lets
 * both callers in: a tile hands over its title, picture and area, the showreel
 * hands over a label and no title at all.
 *
 * It opens playing, with the browser's own controls — no play button of its
 * own. The click that opened it was already the "watch this" gesture, so
 * asking for a second one inside the box would only add a step.
 *
 * UiModal underneath is what makes it a dialog rather than a fixed div: focus
 * trap, Escape, backdrop click and the scroll lock all come from there.
 */
function UiMediaLightbox({
    video,
    poster,
    title,
    area,
    label,
    open,
    onClose,
    onClosed,
    closeLabel = 'Schließen',
}: UiMediaLightboxProps) {
    const videoRef = useRef<HTMLVideoElement>(null);

    const videoUrl = video?.asset?.url;
    const posterUrl = sanityImage.heroPoster(poster);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        // `autoPlay` alone is not enough: a browser refuses it whenever it does
        // not credit the click that opened this dialog, and refuses silently.
        // So play is asked for explicitly, and a refusal is stepped down rather
        // than left as a black box — muted always plays, and the controls then
        // show the muted state, one click from the sound.
        video.muted = false;

        void video.play().catch(() => {
            video.muted = true;

            void video.play().catch(() => {
                // Refused outright. The poster stands with its controls and the
                // viewer presses play — which is a first-party gesture no
                // policy can turn down.
            });
        });
    }, [videoUrl]);

    if (!videoUrl && !posterUrl) return null;

    return (
        <UiModal
            open={open}
            onClose={onClose}
            onClosed={onClosed}
            appear
            hideClose
            aria-label={label ?? title ?? area?.label ?? closeLabel}
            containerClassName="!items-center"
            /* A utility, not the stylesheet: UiModal's backdrop carries
               `bg-black/70` as a utility of its own, and only another utility
               beats it. 0.9 is the design's scrim, and it comes with no blur —
               at that depth there is nothing left to see through, so a blur
               would cost a compositor pass and show nothing for it. */
            backdropClassName="!bg-[rgb(0_0_0_/_0.9)]"
            /* The width itself is --media-lightbox-width, next to the rest of
               the box metrics in components/media-lightbox.css. */
            panelClassName="media-lightbox__panel !bg-black !h-auto !w-(--media-lightbox-width) !max-w-(--media-lightbox-width)"
            /* The box never scrolls — it is sized to the window — so the body
               steps out of the way and the film fills the panel itself. */
            bodyClassName="contents"
        >
            {videoUrl ? (
                <video
                    ref={videoRef}
                    className="media-lightbox__video"
                    controls
                    autoPlay
                    playsInline
                    poster={posterUrl}
                    preload="auto"
                >
                    <source src={videoUrl} type={video?.asset?.mimeType ?? undefined} />
                </video>
            ) : posterUrl ? (
                <Image
                    src={posterUrl}
                    alt={resolveImageAlt(poster, title ?? '')}
                    fill
                    sizes="(max-width: 1022px) 90vw, 920px"
                    className="media-lightbox__poster"
                />
            ) : null}

            {title || area ? (
                <div className="media-lightbox__caption">
                    {title ? <span className="media-lightbox__title">{title}</span> : null}

                    {area ? (
                        <span className="media-lightbox__area" style={{ '--area': area.color } as CSSProperties}>
                            <span className="area-mark" aria-hidden="true" />
                            {area.label}
                        </span>
                    ) : null}
                </div>
            ) : null}

            <button type="button" className="media-lightbox__close" onClick={onClose} aria-label={closeLabel}>
                <IconXMark />
            </button>
        </UiModal>
    );
}

export default UiMediaLightbox;
